if (!customElements.get('kit-modal')) {
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  customElements.define(
    'kit-modal',
    class KitModal extends HTMLElement {
      connectedCallback() {
        try {
          this.config = JSON.parse(this.querySelector('[data-kit-config]').textContent);
        } catch (e) {
          return;
        }
        this.addons = this.config.addons.filter((a) => a.available);
        if (!this.addons.length) return;

        this.dialog = this.querySelector('dialog');
        this.setupDetail();

        // X, Escape and a click outside close the add-on's page.
        this.querySelector('[data-kit-close]').addEventListener('click', () => this.leavePeek());
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.leavePeek();
        });
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.leavePeek();
        });
        this.dialog.addEventListener('close', () => {
          document.body.classList.remove('overflow-hidden');
          this.resetPeek();
        });

        this.onCartAdded = this.onCartAdded.bind(this);
        document.addEventListener('cts:cart-added', this.onCartAdded);
        this.whenStyled().then(() => {
          if (this.isConnected) this.setupOffer();
        });
      }

      disconnectedCallback() {
        // The package cards and the button total live outside this element,
        // so a re-render (eg in the theme editor) would otherwise leave them
        // behind next to the new ones.
        this.offerEl?.remove();
        this.offerForm?.querySelector('.kit-offer__total')?.remove();
        document.removeEventListener('cts:cart-added', this.onCartAdded);
        this.offerUnsubscriber?.();
        if (this.onWhyOutside) document.removeEventListener('click', this.onWhyOutside);
        if (this.onWhyKey) document.removeEventListener('keydown', this.onWhyKey);
        window.removeEventListener('popstate', this.onPopState);
        if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
      }

      // kit-modal.css loads without pausing the page (media="print" swapped to
      // "all" on load), so the cards wait for it rather than flash unstyled.
      whenStyled() {
        const link = document.querySelector('link[data-kit-css]');
        if (!link || link.media === 'all') return Promise.resolve();
        return new Promise((resolve) => link.addEventListener('load', resolve, { once: true }));
      }

      // Keeps Add to cart disabled with its spinner while the kit is added
      // and the shopper is sent to the cart, so a second tap can't add the
      // unit again. product-form.js re-enables the button as soon as the
      // unit is in, so this runs just after that.
      holdSubmit(on) {
        const form = document.querySelector(`product-info[data-product-id="${this.config.productId}"] product-form`);
        const button = form?.querySelector('.product-form__submit');
        if (!button) return;
        if (on) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
        button.classList.toggle('loading', on);
        form.querySelector('.loading__spinner')?.classList.toggle('hidden', !on);
      }

      // "Unit only" leaves the cart-page redirect to product-form.js. A kit
      // goes into the cart with the unit before the shopper is sent there.
      // Either way Add to cart stays held until the page leaves, so a second
      // tap can't add the unit again.
      onCartAdded(event) {
        const detail = event.detail || {};
        if (detail.form_source !== 'product-form') return;
        if (String(detail.product_id) !== String(this.config.productId)) return;
        setTimeout(() => this.holdSubmit(true));
        if (!this.onPageShow) {
          // Coming back from the cart with the browser's Back button restores
          // the page as it was, button still held.
          this.onPageShow = (e) => {
            if (e.persisted) this.holdSubmit(false);
          };
          window.addEventListener('pageshow', this.onPageShow);
        }
        const ids = this.offerIds();
        if (!ids.length) {
          if (this.offerEl) this.track('added_to_cart', { package: 'unit_only', items: '' });
          return;
        }
        event.preventDefault();
        this.addKit(ids);
      }

      // One set per click, even when the cart already holds a set (eg a
      // second car); the cart page prices one kit set per unit. If the
      // connection drops, the cart is checked before trying again so a set
      // that did go in isn't added twice. A refused add (eg an item sold out)
      // isn't retried: the shopper stays on the page with a note and can try
      // again or go to the cart, where the unit already is.
      async addKit(ids) {
        this.showKitError(null);
        this.holdSubmit(true);
        const items = ids.map((id) => ({ id: this.cartIdOf(id), quantity: 1 }));
        const before = await this.kitQuantities(items);
        let added = await this.postKit(items);
        if (added === null && before) {
          const now = await this.kitQuantities(items);
          if (now && items.every((item) => now[item.id] > before[item.id])) added = true;
          else if (now) added = await this.postKit(items);
        }
        const kitIds = this.offerKit?.variantIds || [];
        if (added === true) {
          this.track('added_to_cart', {
            package: ids.length < kitIds.length ? 'custom_kit' : 'kit',
            items: this.namesOf(ids),
          });
          window.location = routes.cart_url;
          return;
        }
        this.track('kit_add_failed', { items: this.namesOf(ids) });
        this.holdSubmit(false);
        this.showKitError(ids);
      }

      // true once added, false when Shopify refuses it, null when the
      // connection fails or takes over 10 seconds.
      async postKit(items) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 10000);
        try {
          const res = await fetch(`${routes.cart_add_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
            signal: controller.signal,
          });
          return res.ok;
        } catch (e) {
          return null;
        } finally {
          clearTimeout(timer);
        }
      }

      // How many of each kit item the cart holds, or null if it can't be read.
      async kitQuantities(items) {
        try {
          const cart = await fetch(`${routes.cart_url}.js`).then((r) => r.json());
          const counts = {};
          items.forEach((item) => {
            counts[item.id] = 0;
          });
          cart.items.forEach((line) => {
            if (line.variant_id in counts) counts[line.variant_id] += line.quantity;
          });
          return counts;
        } catch (e) {
          return null;
        }
      }

      // The note above Add to cart when the kit couldn't be added.
      showKitError(ids) {
        const box = this.offerEl?.querySelector('.kit-offer__error');
        if (!box) return;
        box.hidden = !ids;
        if (!ids) return;
        const unit = (this.config.unitLabel || '').replace(/\s+only$/i, '').toLowerCase() || 'unit';
        box.querySelector('.kit-offer__error-text').textContent =
          `We couldn't add the kit. Your ${unit} is already in your cart.`;
        box.querySelector('.kit-offer__retry').onclick = () => this.addKit(ids);
      }

      dismiss() {
        if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;
        if (reduceMotion()) {
          this.dialog.close();
          return;
        }
        this.dialog.classList.add('is-closing');
        setTimeout(() => {
          this.dialog.close();
          this.dialog.classList.remove('is-closing');
        }, 180);
      }

      addon(variantId) {
        return this.addons.find((a) => a.variantId === variantId);
      }

      // Analytics: cts-product-analytics.js turns these into KIT_* events.
      track(action, props = {}) {
        document.dispatchEvent(new CustomEvent('cts:kit-interaction', { detail: { action, ...props } }));
      }

      namesOf(ids) {
        return ids.map((id) => this.addon(id)?.title).filter(Boolean).join(', ');
      }

      // What goes in the cart for an add-on bought with the unit: its kit
      // copy at the kit price, or the add-on itself when it has none.
      cartIdOf(variantId) {
        const addon = this.addon(variantId);
        return addon?.kitVariantId || variantId;
      }

      subtotalOf(ids) {
        return ids.reduce((sum, id) => sum + (this.addon(id)?.price || 0), 0);
      }

      // What the shopper pays for an add-on bought with the unit.
      payOf(addon) {
        return addon.kitPrice ?? addon.price;
      }

      savingOf(ids) {
        const bundle = ids.reduce((sum, id) => {
          const addon = this.addon(id);
          return addon ? sum + addon.price - this.payOf(addon) : sum;
        }, 0);
        return bundle;
      }

      // Formats like the store's own money filter in the shopper's currency,
      // read from a sample the section renders (1234.56), so symbol, its
      // position and separators match the rest of the site. Whole amounts
      // drop their decimals.
      money(cents) {
        if (this.moneyParts === undefined) this.moneyParts = this.parseMoneySample(this.config.moneySample);
        const parts = this.moneyParts;
        const amount = Math.round(cents) / 100;
        if (!parts) {
          return '$' + amount.toLocaleString('en-US', {
            minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
            maximumFractionDigits: 2,
          });
        }
        const decimals = parts.decimals && !Number.isInteger(amount) ? parts.decimals : 0;
        const [whole, fraction] = Math.abs(amount).toFixed(decimals).split('.');
        const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, parts.group);
        const number = fraction ? grouped + parts.decimal + fraction : grouped;
        return (amount < 0 ? '-' : '') + parts.before + number + parts.after;
      }

      parseMoneySample(sample) {
        if (!sample) return null;
        // The format can carry HTML entities or tags, eg &euro; or a span.
        const text = new DOMParser().parseFromString(sample, 'text/html').documentElement.textContent.trim();
        const match = text.match(/^([^\d]*)1([^\d]?)23([45])(?:([^\d])(\d+))?([^\d]*)$/);
        if (!match) return null;
        return {
          before: match[1],
          group: match[2],
          decimal: match[4] || '.',
          decimals: match[5] ? match[5].length : 0,
          after: match[6],
        };
      }

      // Package cards on the product page, above Add to cart: the unit alone,
      // then each package block. The page opens on the unit alone or on the
      // main (highlighted) package, per the section's "Selected when the page
      // opens" setting.
      setupOffer() {
        const pid = this.config.productId;
        this.offerForm = document.querySelector(`product-info[data-product-id="${pid}"] product-form`);
        this.offerPackages = this.config.packages
          .map((pkg) => ({ ...pkg, variantIds: pkg.variantIds.filter((id) => this.addon(id)) }))
          .filter((pkg) => pkg.variantIds.length);
        if (!this.offerForm || !this.offerPackages.length) return;
        const best = this.offerPackages.find((pkg) => pkg.highlight) || this.offerPackages[this.offerPackages.length - 1];
        this.offerChoice = this.config.defaultPackage === 'kit' ? best : null;

        // The highlighted kit can be customized: every item starts ticked.
        this.offerKit = best;
        this.offerPick = new Set(best.variantIds);
        const offer = document.createElement('div');
        offer.className = 'kit-offer';
        offer.innerHTML = `
          <span class="form__label kit-offer__label" id="KitOfferLabel-${pid}">Package</span>
          <div class="kit-offer__cards" role="radiogroup" aria-labelledby="KitOfferLabel-${pid}"></div>
          <p class="kit-offer__note" hidden></p>
          <div class="kit-offer__error" role="alert" hidden>
            <span class="kit-offer__error-text"></span>
            <span class="kit-offer__error-actions">
              <button type="button" class="kit-offer__retry">Try again</button>
              <a class="kit-offer__to-cart" href="${routes.cart_url}">Go to cart</a>
            </span>
          </div>`;
        const cards = offer.querySelector('.kit-offer__cards');
        [null, ...this.offerPackages].forEach((pkg) => {
          // The label covers the card's top part; the kit's checklist and
          // "Customize kit" sit outside it, so ticking never changes the package.
          const card = document.createElement('div');
          card.className = 'kit-offer__card';
          card.innerHTML = `
            <label class="kit-offer__hit">
              <input type="radio" class="kit-offer__input" name="KitOffer-${pid}">
              <span class="kit-offer__radio" aria-hidden="true"></span>
              <span class="kit-offer__body">
                <span class="kit-offer__name"></span>
              </span>
              <span class="kit-offer__side">
                <span class="kit-offer__prices">
                  <s class="kit-offer__was"></s>
                  <span class="kit-offer__price"></span>
                </span>
                <span class="kit-offer__save"></span>
              </span>
              <span class="kit-offer__about">
                <span class="kit-offer__caption"></span>
              </span>
            </label>
            <div class="kit-offer__thumbs"></div>`;
          card.querySelector('.kit-offer__name').textContent = pkg ? pkg.title : this.config.unitLabel;
          this.writeCaption(card.querySelector('.kit-offer__caption'), pkg?.caption);
          const thumbs = card.querySelector('.kit-offer__thumbs');
          thumbs.hidden = pkg !== best;
          // Each photo opens the add-on's page, without changing the package.
          if (pkg === best) {
            pkg.variantIds.forEach((id) => {
              const addon = this.addon(id);
              if (!addon.image) return;
              const thumb = document.createElement('button');
              thumb.type = 'button';
              thumb.className = 'kit-offer__thumb';
              thumb.dataset.variantId = id;
              thumb.setAttribute('aria-label', `View details: ${addon.title}`);
              const img = document.createElement('img');
              img.src = addon.image;
              img.alt = '';
              img.loading = 'lazy';
              img.width = 44;
              img.height = 44;
              thumb.appendChild(img);
              thumb.addEventListener('click', () => this.openPeek(addon, thumb, 'photo'));
              thumbs.appendChild(thumb);
            });
          }
          const was = card.querySelector('.kit-offer__was');
          const save = card.querySelector('.kit-offer__save');
          if (pkg) {
            const list = this.subtotalOf(pkg.variantIds);
            const pay = this.offerPay(pkg);
            card.querySelector('.kit-offer__price').textContent = `+${this.money(pay)}`;
            was.textContent = `+${this.money(list)}`;
            save.textContent = `save ${this.money(list - pay)}`;
            was.hidden = save.hidden = list <= pay;
            if (pkg === best) this.setupCustomize(card);
          } else {
            card.querySelector('.kit-offer__price').dataset.kitOfferUnit = '';
            was.hidden = true;
            save.hidden = true;
          }
          const input = card.querySelector('input');
          input.checked = pkg === this.offerChoice;
          card.classList.toggle('is-selected', input.checked);
          input.addEventListener('change', () => {
            this.offerChoice = pkg;
            this.showKitError(null);
            this.track('package_switched', { package: pkg ? 'kit' : 'unit_only' });
            cards.querySelectorAll('.kit-offer__card').forEach((other) =>
              other.classList.toggle('is-selected', other === card)
            );
            // Picking another package closes the kit's checklist, so its
            // ticks don't read as still included.
            if (pkg !== this.offerKit && this.offerList) this.showCustomize(false);
            this.updateOfferTotal();
          });
          // The card's padding picks it too, like the rest of the card.
          card.addEventListener('click', (event) => {
            if (event.target === card) input.click();
          });
          if (!pkg) this.offerUnitInput = input;
          cards.appendChild(card);
        });

        // With two or more packages, show how little the best one adds over
        // the one before it, eg "Only $14 more than the starter kit for the front camera".
        const index = this.offerPackages.indexOf(best);
        const previous = this.offerPackages[index - 1];
        if (previous) {
          const extra = this.offerPay(best) - this.offerPay(previous);
          const names = best.variantIds.filter((id) => !previous.variantIds.includes(id)).map((id) => this.addon(id).title);
          const note = offer.querySelector('.kit-offer__note');
          note.textContent = `Only ${this.money(extra)} more than the ${previous.title.toLowerCase()} for the ${names.join(' and ')}`;
          note.hidden = !names.length || extra <= 0;
        }

        this.offerForm.parentElement.before(offer);
        this.offerEl = offer;
        this.offerQuantity = document.querySelector(`#Quantity-${this.offerForm.dataset.sectionId}`);
        this.offerQuantity?.addEventListener('change', () => this.updateOfferTotal());
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.offerUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => this.updateOfferTotal());
        }
        this.updateOfferTotal();
      }

      // What a package costs on top of the unit, at with-unit prices.
      offerPay(pkg) {
        return this.payFor(pkg.variantIds);
      }

      payFor(ids) {
        return this.subtotalOf(ids) - this.savingOf(ids);
      }

      // The add-ons the picked package puts in the cart: the kit's ticked
      // items, or another package's full list.
      offerIds() {
        if (!this.offerChoice) return [];
        if (this.offerChoice === this.offerKit) return this.offerKit.variantIds.filter((id) => this.offerPick.has(id));
        return this.offerChoice.variantIds;
      }

      // A "|" in the caption marks where the line breaks on wide cards.
      writeCaption(el, text) {
        el.textContent = '';
        (text || '').split('|').forEach((part, i) => {
          if (i) el.append(' ', Object.assign(document.createElement('br'), { className: 'kit-offer__break' }));
          el.append(part.trim());
        });
        el.hidden = !el.textContent;
      }

      // "Customize kit" opens a checklist inside the kit card, every item
      // ticked. Unticking renames the card "Custom kit" and updates its
      // prices; "View details" opens the add-on's page over the product page.
      setupCustomize(card) {
        const pid = this.config.productId;
        const kit = this.offerKit;
        this.offerKitCard = card;
        const list = document.createElement('div');
        list.className = 'kit-offer__list';
        list.id = `KitOfferList-${pid}`;
        list.hidden = true;
        kit.variantIds.forEach((id) => {
          const addon = this.addon(id);
          const nameId = `KitOfferItem-${pid}-${id}`;
          const row = document.createElement('div');
          row.className = 'kit-offer__row';
          row.dataset.variantId = id;
          row.innerHTML = `
            <label class="kit-offer__pick">
              <input type="checkbox" class="kit-offer__pick-input" aria-labelledby="${nameId}" checked>
              <span class="kit-check" aria-hidden="true">
                <svg viewBox="0 0 20 20" focusable="false"><path class="kit-check__path" d="M5 10.5l3.2 3.2L15 6.8" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </span>
              <span class="kit-offer__item-thumb"></span>
              <span class="kit-offer__item">
                <span class="kit-offer__item-name" id="${nameId}"></span>
                <span class="kit-offer__item-blurb"></span>
              </span>
              <span class="kit-offer__item-price"></span>
            </label>
            <button type="button" class="kit-item__details kit-offer__details">view details<span class="visually-hidden"></span><svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M4 10h11M11 5.5l4.5 4.5-4.5 4.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></button>`;
          row.querySelector('.kit-offer__item-name').textContent = addon.title;
          const blurb = row.querySelector('.kit-offer__item-blurb');
          blurb.textContent = addon.blurb || '';
          blurb.hidden = !addon.blurb;
          if (addon.image) {
            const img = document.createElement('img');
            img.src = addon.image;
            img.alt = '';
            img.loading = 'lazy';
            img.width = 44;
            img.height = 44;
            row.querySelector('.kit-offer__item-thumb').appendChild(img);
          }
          // "~~$49~~ $25"; phones stack the two prices to leave the name
          // more room.
          this.writePrice(row.querySelector('.kit-offer__item-price'), addon);
          row.querySelector('input').addEventListener('change', (event) => this.pickOffer(id, event.target.checked));
          const details = row.querySelector('.kit-offer__details');
          details.querySelector('.visually-hidden').textContent = `: ${addon.title}`;
          details.addEventListener('click', () => this.openPeek(addon, details));
          // The photo opens the add-on's page too, instead of ticking the box.
          row.querySelector('.kit-offer__item-thumb').addEventListener('click', (event) => {
            event.preventDefault();
            this.openPeek(addon, details, 'photo');
          });
          list.appendChild(row);
        });

        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'kit-offer__customize';
        toggle.setAttribute('aria-controls', list.id);
        toggle.setAttribute('aria-expanded', 'false');
        toggle.innerHTML = `<span></span><svg viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        toggle.addEventListener('click', () => {
          const input = card.querySelector('.kit-offer__input');
          if (!input.checked) input.click();
          this.track(list.hidden ? 'customize_opened' : 'customize_hidden');
          this.showCustomize(list.hidden);
        });
        // "Customize kit" on the left; "Why the discount?" on the right
        // opens a small bubble above it with the reason for the kit prices.
        const links = document.createElement('div');
        links.className = 'kit-offer__links';
        links.append(toggle);
        if (this.config.whyText) {
          const why = document.createElement('button');
          why.type = 'button';
          why.className = 'kit-offer__why';
          why.textContent = this.config.whyLabel || 'Why the discount?';
          const pop = document.createElement('p');
          pop.className = 'kit-offer__pop';
          pop.id = `KitOfferWhy-${pid}`;
          pop.textContent = this.config.whyText;
          pop.hidden = true;
          why.setAttribute('aria-controls', pop.id);
          why.setAttribute('aria-expanded', 'false');
          const show = (open) => {
            pop.hidden = !open;
            why.setAttribute('aria-expanded', String(open));
          };
          why.addEventListener('click', (event) => {
            event.stopPropagation();
            if (pop.hidden) this.track('why_clicked');
            show(pop.hidden);
          });
          // Any tap outside the bubble, or Escape, closes it.
          this.onWhyOutside = (event) => {
            if (!pop.hidden && !pop.contains(event.target)) show(false);
          };
          this.onWhyKey = (event) => {
            if (event.key === 'Escape' && !pop.hidden) {
              show(false);
              why.focus();
            }
          };
          document.addEventListener('click', this.onWhyOutside);
          document.addEventListener('keydown', this.onWhyKey);
          links.append(why, pop);
        }
        card.append(list, links);
        this.offerList = list;
        this.offerToggle = toggle;
        this.showCustomize(false);
      }

      showCustomize(open) {
        this.offerList.hidden = !open;
        this.offerKitCard.querySelector('.kit-offer__thumbs').hidden = open;
        this.offerToggle.setAttribute('aria-expanded', String(open));
        this.offerToggle.querySelector('span').textContent = open ? 'Hide' : 'Customize kit';
      }

      // Ticks or unticks a kit item. Unticking everything picks the unit
      // alone and resets the kit to every item.
      pickOffer(id, on) {
        if (on) this.offerPick.add(id);
        else this.offerPick.delete(id);
        if (!this.offerPick.size) {
          this.offerKit.variantIds.forEach((each) => this.offerPick.add(each));
          this.showCustomize(false);
          this.offerUnitInput?.click();
        }
        this.renderKitCard();
        this.updateOfferTotal();
      }

      renderKitCard() {
        const kit = this.offerKit;
        const card = this.offerKitCard;
        const ids = kit.variantIds.filter((id) => this.offerPick.has(id));
        const full = ids.length === kit.variantIds.length;
        const list = this.subtotalOf(ids);
        const pay = this.payFor(ids);
        card.querySelector('.kit-offer__name').textContent = full ? kit.title : 'Custom kit';
        this.writeCaption(
          card.querySelector('.kit-offer__caption'),
          full ? kit.caption : `${ids.length} of ${kit.variantIds.length} items`
        );
        card.querySelector('.kit-offer__price').textContent = `+${this.money(pay)}`;
        const was = card.querySelector('.kit-offer__was');
        const save = card.querySelector('.kit-offer__save');
        was.textContent = `+${this.money(list)}`;
        save.textContent = `save ${this.money(list - pay)}`;
        was.hidden = save.hidden = list <= pay;
        card.querySelectorAll('.kit-offer__row').forEach((row) => {
          const on = this.offerPick.has(Number(row.dataset.variantId));
          row.querySelector('input').checked = on;
          row.classList.toggle('is-off', !on);
        });
        card.querySelectorAll('.kit-offer__thumb').forEach((thumb) => {
          thumb.hidden = !this.offerPick.has(Number(thumb.dataset.variantId));
        });
      }

      // The unit's price on its card and "Add to cart · $673" on the button
      // follow the selected variant, quantity and package.
      updateOfferTotal() {
        if (!this.offerEl) return;
        const id = this.offerForm.querySelector('[name="id"]')?.value;
        const unit = Number(this.config.variantPrices[id]) || 0;
        const quantity = Math.max(1, Number(this.offerQuantity?.value) || 1);
        this.offerEl.querySelector('[data-kit-offer-unit]').textContent = this.money(unit);

        const button = this.offerForm.querySelector('.product-form__submit');
        if (!button) return;
        let total = button.querySelector('.kit-offer__total');
        if (!total) {
          total = document.createElement('span');
          total.className = 'kit-offer__total';
          button.querySelector('span')?.after(total);
        }
        const pay = this.payFor(this.offerIds());
        total.textContent = unit && !button.disabled ? ` · ${this.money(unit * quantity + pay)}` : '';
      }

      // "$25" with the regular price crossed out before it ("$49") when the
      // add-on is cheaper bought with the unit.
      writePrice(el, addon) {
        el.textContent = this.money(this.payOf(addon));
        if (addon.kitPrice == null) return;
        const was = document.createElement('s');
        was.className = 'kit-item__was';
        was.textContent = this.money(addon.price);
        el.prepend(was, ' ');
      }

      // Add-on page only: "+$40" first, the regular price crossed out after
      // it, and "save $59" in neon on the line below.
      writeDetailPrice(addon) {
        const el = this.querySelector('[data-kit-detail-price]');
        const pay = this.payOf(addon);
        el.textContent = `+${this.money(pay)}`;
        const save = this.querySelector('[data-kit-detail-save]');
        const cheaper = addon.kitPrice != null && addon.price > pay;
        save.hidden = !cheaper;
        if (!cheaper) return;
        const was = document.createElement('s');
        was.className = 'kit-item__was';
        was.textContent = `+${this.money(addon.price)}`;
        el.append(' ', was);
        save.textContent = `save ${this.money(addon.price - pay)}`;
      }

      // The add-on's page takes a history entry so the phone's Back gesture
      // closes it instead of leaving the product page.
      setupDetail() {
        this.detail = this.querySelector('[data-kit-detail]');
        this.slides = this.querySelector('[data-kit-slides]');
        // Tags our history entry, so an entry left by a reload is ignored.
        this.historyKey = `kit-${Date.now()}`;
        this.querySelector('[data-kit-prev]').addEventListener('click', () => this.goToSlide(this.slideIndex - 1));
        this.querySelector('[data-kit-next]').addEventListener('click', () => this.goToSlide(this.slideIndex + 1));
        this.slides.addEventListener('scroll', () => {
          cancelAnimationFrame(this.slideFrame);
          this.slideFrame = requestAnimationFrame(() => this.syncSlide());
        });
        this.onPopState = () => {
          if (this.peek) this.dismiss();
        };
        window.addEventListener('popstate', this.onPopState);
      }

      fillDetail(addon) {
        this.querySelector('[data-kit-detail-title]').textContent = addon.title;
        this.writeDetailPrice(addon);
        const note = this.querySelector('[data-kit-detail-note]');
        note.textContent = addon.note || '';
        note.hidden = !addon.note;
        this.querySelector('[data-kit-detail-desc]').innerHTML = addon.descriptionHtml || '';
        this.renderSlides(addon);
      }

      // "View details" or an add-on's photo on the product page: the modal
      // opens on the add-on's page, for reading only; the checklist adds and
      // removes items. Closing returns to the product page.
      openPeek(addon, trigger, from = 'details') {
        this.track('details_viewed', { item: addon.title, where: from === 'photo' ? 'kit_photo' : 'page' });
        this.peek = { addon, trigger };
        this.fillDetail(addon);
        this.querySelector('[data-kit-peek-title]').textContent = addon.title;
        this.dialog.classList.remove('is-closing');
        history.pushState({ kitDetail: this.historyKey }, '');
        this.dialog.showModal();
        document.body.classList.add('overflow-hidden');
        // Set after showing: a closed dialog keeps the last item's gallery
        // scroll, which would open on its third photo with "1 / 6" shown.
        this.slides.scrollLeft = 0;
        this.syncSlide();
        this.querySelector('.kit-modal__body').scrollTop = 0;
        this.detail.scrollTop = 0;
        this.detail.focus({ preventScroll: true });
      }

      leavePeek() {
        if (this.ownsHistory()) history.back();
        else this.dismiss();
      }

      // Runs once the dialog has closed.
      resetPeek() {
        if (!this.peek) return;
        const { trigger } = this.peek;
        this.peek = null;
        this.slides.innerHTML = '';
        trigger?.focus({ preventScroll: true });
      }

      ownsHistory() {
        return Boolean(history.state && history.state.kitDetail === this.historyKey);
      }

      renderSlides(addon) {
        const images = addon.images || [];
        const gallery = this.querySelector('[data-kit-gallery]');
        this.slides.innerHTML = '';
        this.slideCount = Math.max(images.length, 1);
        this.slideIndex = 0;
        if (images.length) {
          images.forEach((src, index) => {
            const slide = document.createElement('div');
            slide.className = 'kit-detail__slide';
            const img = document.createElement('img');
            img.src = src;
            img.alt = index === 0 ? addon.title : '';
            img.loading = index === 0 ? 'eager' : 'lazy';
            slide.appendChild(img);
            this.slides.appendChild(slide);
          });
        } else {
          // No photos yet: the same box the list row shows.
          this.slides.innerHTML =
            '<div class="kit-detail__slide kit-detail__slide--empty"><svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z M4 7.5l8 4.5 8-4.5 M12 12v9" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round"/></svg></div>';
        }
        this.slides.scrollLeft = 0;
        gallery.classList.toggle('has-many', this.slideCount > 1);
        this.syncSlide();
      }

      goToSlide(index) {
        const next = Math.max(0, Math.min(this.slideCount - 1, index));
        this.slides.scrollTo({ left: next * this.slides.clientWidth, behavior: reduceMotion() ? 'auto' : 'smooth' });
      }

      syncSlide() {
        const width = this.slides.clientWidth || 1;
        this.slideIndex = Math.round(this.slides.scrollLeft / width);
        this.querySelector('[data-kit-slide-count]').textContent = `${this.slideIndex + 1} / ${this.slideCount}`;
        this.querySelector('[data-kit-prev]').disabled = this.slideIndex === 0;
        this.querySelector('[data-kit-next]').disabled = this.slideIndex >= this.slideCount - 1;
      }
    }
  );
}
